import moment = require("moment-timezone");
// tslint:disable-next-line:no-implicit-dependencies
import {Subject} from "rxjs";
import {PossibleContent} from "./components/HaccpTable";
import {Record} from "./components/HaccpTableRow";
import {HaccpService} from "./HaccpService";

export enum UnitOfMeasure {
    CELSIUS = "CELSIUS",
    FAHRENHEIT = "FAHRENHEIT",
    KELVIN = "KELVIN",
}

export let DEFAULT_TEMPERATURE_UNIT = UnitOfMeasure.CELSIUS;

export const DEFAULT_OTHER_PRODUCT_CATEGORY_GUID = "51d6d642-caf5-11e8-9c0a-9418826ed820";

export const setDefaultTemperatureUnit = (unit: UnitOfMeasure) => DEFAULT_TEMPERATURE_UNIT = unit;

const DEFAULT_GRANULARITY = "HOURLY";
export const DEFAULT_TIME_UNIT = "day";

const SINGLE_REPORT_NAME = "HACCP compliance report";
const ALL_REPORT_NAME = "HACCP compliance report";

const HOT_TEXT_COLOR_THRESHOLD = 0;
const COLD_TEXT_COLOR_THRESHOLD = 4;

export const HaccpColours = {
    hotterDeviation: ["rgb(240,191,193)", "rgb(243,172,174)", "rgb(241,160,163)", "rgb(238,137,149)"],
    colderDeviation: ["rgb(136,174,206)", "rgb(159,190,215)", "rgb(171,198,220)", "rgb(220,231,237)"],
    cleaning: "rgb(47,186,156)",
    defrost: "rgb(254,138,1)",
    noData: "rgb(255,255,255)",
    off: "rgb(123,137,145)",
    inRange: "rgb(255,255,255)",
};

export const numberOfDays = (a: Date, b: Date) => {
    if (!a || !b) {
        return 1;
    }
    const diff = moment(a)
        .add(1, DEFAULT_TIME_UNIT)
        .startOf(DEFAULT_TIME_UNIT)
        .diff(
            moment(b).startOf(DEFAULT_TIME_UNIT),
            DEFAULT_TIME_UNIT,
        );
    return diff;
};

export const fillArray = <T>(length: number, value: any): T[] => {
    const array: T[] = [];
    for (let i = 0; i < length; i++) {
        array.push(value);
    }
    return array;
};

export interface DiagnosticType {
    aggregated: boolean;
    categoryGuid: string;
    categoryName: string;
    format: string;
    guid: string;
    name: string;
    order: number;
    supportsHaccp: boolean;
}

export type SourceDiagnosticType = DiagnosticType;

export interface TargetDiagnosticType extends DiagnosticType {
    sourceDiagnosticType: SourceDiagnosticType;
}

export interface ProductCategory {
    guid: string;
    name: string;
    minTemperature: number;
    maxTemperature: number;
}

export interface HaccpConfiguration {
    haccpConfigurationGuid: string;
    equipmentGuid: string;
    minTemperature: number;
    maxTemperature: number;
    productCategory: ProductCategory;
    targetDiagnosticType: TargetDiagnosticType;
    records?: Record[];
    name?: string;
}

export interface LocationHaccpResponse {
    configurations: (HaccpConfigurationWithReports | HaccpConfiguration)[];
    diagnosticTypes: DiagnosticType[];
    equipment: {
        model: EquipmentModel;
        guid: string;
    };
    installationDate: string;
}

export interface HaccpConfigurationWithReports {
    report: any;
    configuration: HaccpConfiguration;
}

export interface EquipmentModel {
    category: string;
    categoryGuid: string;
    categoryImage: string;
    coffeeMachine: true;
    dummy: boolean;
    generic: boolean;
    guid: string;
    manufacturerName: string;
    name: string;
    saleUnitOfMeasure: string;
    type: string;
    typeGeneric: true;
    typeGuid: string;
    typeImage: string;
}

// The function returns the ceil of a number to a certain fractionDigit
// Needed for coloring the cell correctly
// Ex: mean_value =  1.034567...
// mean_value.toFixed(1)  returns a string: "1.0"
// ceilFixed(mean_value, 1) return a number: 1.1
export const ceilFixed = (x: number, fractionDigits: number = 0): number => {
    const powerOf10 = 10 ** fractionDigits;
    return Math.ceil(x * powerOf10) / powerOf10;
};

export class HaccpController {

    public static models: any[];

    private static assignColorToHaccpConfigRecords = (haccpConfig: HaccpConfiguration, colorConfiguration: any) => {

        const contained = (x, a, b) => {
            return (x >= a && x <= b) || (x >= b && x <= a);
        };

        const findClosestIndex = (x: number, vett: number[]): number => {
            for (let i = 0; i < vett.length - 1; i++) {
                if (contained(x, vett[i], vett[i + 1])) {
                    return i;
                }
            }
            return vett.length - 1;
        };

        HaccpColours.colderDeviation = colorConfiguration[0].colderColours;
        HaccpColours.hotterDeviation = colorConfiguration[0].hotterColours;

        HaccpColours.defrost = colorConfiguration[0].defrostOnColour;
        HaccpColours.noData = colorConfiguration[0].missingColour;
        HaccpColours.inRange = colorConfiguration[0].inRangeColour;

        haccpConfig.records.forEach((record) => {

            const minV = ceilFixed(colorConfiguration[record.configurationId].minValidRange as number, 1);
            const maxV = ceilFixed(colorConfiguration[record.configurationId].maxValidRange as number, 1);

            const hotterSteps = [...(colorConfiguration[record.configurationId].hotterSteps as number[])];
            const colderSteps = [...(colorConfiguration[record.configurationId].colderSteps as number[])];

            const hotterColours = HaccpColours.hotterDeviation as string[];
            const colderColours = HaccpColours.colderDeviation as string[];

            hotterSteps.unshift(maxV);
            hotterSteps.push(Infinity);

            colderSteps.unshift(-Infinity);
            colderSteps.push(minV);

            record.mean = record.mean ? ceilFixed(record.mean, 1) : undefined;
            record.hi = ceilFixed(colorConfiguration[record.configurationId].maxValidRange as number, 1);
            record.lo = ceilFixed(colorConfiguration[record.configurationId].minValidRange as number, 1);
            record.currentMax = ceilFixed(haccpConfig.maxTemperature, 1);
            record.currentMin = ceilFixed(haccpConfig.minTemperature, 1);

            record.thresholdsChanged = record.hi !== record.currentMax || record.lo !== record.currentMin;

            if (!record.mean) {
                record.color = HaccpColours.noData;
                record.textColor = "black";
            } else if (record.mean >= record.lo && record.mean <= record.hi) {
                record.color = HaccpColours.inRange;
                record.textColor = "black";
            } else if (record.mean < record.lo) {
                const coldColorIndex = findClosestIndex(record.mean, colderSteps);
                record.color = colderColours[coldColorIndex];
                record.textColor = coldColorIndex < COLD_TEXT_COLOR_THRESHOLD ? "white" : "black";
            } else if (record.mean > record.hi) {
                const hotColorIndex = findClosestIndex(record.mean, hotterSteps);
                record.color = hotterColours[hotColorIndex];
                record.textColor = hotColorIndex > HOT_TEXT_COLOR_THRESHOLD ? "white" : "black";
            }

            if (record.max >= record.lo && record.max <= record.hi) {
                record.maxBgColor = HaccpColours.inRange;
                record.maxTxColor = "black";
            } else if (record.max < record.lo) {
                const coldColorIndex = findClosestIndex(record.max, colderSteps);
                record.maxBgColor = colderColours[coldColorIndex];
                record.maxTxColor = coldColorIndex < COLD_TEXT_COLOR_THRESHOLD ? "white" : "black";
            } else if (record.max > record.hi) {
                const hotColorIndex = findClosestIndex(record.max, hotterSteps);
                record.maxBgColor = hotterColours[hotColorIndex];
                record.maxTxColor = hotColorIndex > HOT_TEXT_COLOR_THRESHOLD ? "white" : "black";
            }

            if (record.min >= record.lo && record.min <= record.hi) {
                record.minBgColor = HaccpColours.inRange;
                record.minTxColor = "black";
            } else if (record.min < record.lo) {
                const coldColorIndex = findClosestIndex(record.min, colderSteps);
                record.minBgColor = colderColours[coldColorIndex];
                record.minTxColor = coldColorIndex < COLD_TEXT_COLOR_THRESHOLD ? "white" : "black";
            } else if (record.min > record.hi) {
                const hotColorIndex = findClosestIndex(record.min, hotterSteps);
                record.minBgColor = hotterColours[hotColorIndex];
                record.minTxColor = hotColorIndex > HOT_TEXT_COLOR_THRESHOLD ? "white" : "black";
            }

            if (record.defrostOn) {
                record.color = HaccpColours.defrost;
                record.textColor = "white";
                record.maxBgColor = HaccpColours.defrost;
                record.minBgColor = HaccpColours.defrost;
                record.minTxColor = "white";
                record.maxTxColor = "white";
            }
        });

        return haccpConfig;
    }

    public equipmentGUID: string;

    public equipmentTimezone: string;

    public installationDate: Subject<Date>;

    private range: { start: Date, end: Date };

    private haccpDate: Subject<{ start: Date, end: Date }>;

    private extremesVisibility: boolean;

    private showExtremes: Subject<boolean>;

    constructor() {
        this.haccpDate = new Subject<{ start: Date, end: Date }>();
        this.showExtremes = new Subject<boolean>();
        this.installationDate = new Subject<Date>();
    }

    public getExtremesVisibility = () => this.extremesVisibility;

    public getDateRange = () => this.range;

    public createNewHaccpConfiguration = (diagnosticGUID, content: PossibleContent) => {
        return HaccpService.createNewHaccpConfiguration(
            this.equipmentGUID,
            diagnosticGUID,
            content.minTemperature,
            content.maxTemperature,
            content.guid,
            DEFAULT_TEMPERATURE_UNIT,
        ).then((result) => {
            this.changeDate(this.range);
            return result;
        });
    }

    public deleteHaccpConfiguration = (diagnosticGUID, equipmentGuid?) => {
        return HaccpService.deleteHaccpConfiguration(
            (equipmentGuid)
                ? equipmentGuid
                : this.equipmentGUID,
            diagnosticGUID)
            .then((result) => {
                this.changeDate(this.range);
                return result;
            });
    }

    public editHaccpConfiguration = (diagnosticGUID, content: PossibleContent) => {
        return HaccpService.editHaccpConfiguration(
            this.equipmentGUID,
            diagnosticGUID,
            content.minTemperature,
            content.maxTemperature,
            content.guid,
            DEFAULT_TEMPERATURE_UNIT,
        ).then((result) => {
            this.changeDate(this.range);
            return result;
        });
    }

    public downloadAllReportExcel = (equipmentGuid?) => {
        return HaccpService.exportAllReports(
            (equipmentGuid) ? equipmentGuid : this.equipmentGUID,
            DEFAULT_GRANULARITY,
            DEFAULT_TEMPERATURE_UNIT,
            moment.tz(
                moment(this.range.start === null ? undefined : this.range.start)
                    .startOf(DEFAULT_TIME_UNIT)
                    .format()
                    .slice(0, 19),
                this.equipmentTimezone,
            ).format(),
            moment.tz(
                moment(this.range.end === null ? undefined : this.range.end)
                    .endOf(DEFAULT_TIME_UNIT)
                    .format()
                    .slice(0, 19),
                this.equipmentTimezone,
            ).format(),
            ALL_REPORT_NAME,
        );
    }

    public downloadReportExcel = (diagnosticGUID, equipmentGuid?): Promise<any> => {
        return HaccpService.exportHaccpConfigReport(
            (equipmentGuid) ? equipmentGuid : this.equipmentGUID,
            diagnosticGUID,
            DEFAULT_GRANULARITY,
            DEFAULT_TEMPERATURE_UNIT,
            moment.tz(
                moment(this.range.start === null ? undefined : this.range.start)
                    .startOf(DEFAULT_TIME_UNIT)
                    .format()
                    .slice(0, 19),
                this.equipmentTimezone,
            ).format(),
            moment.tz(
                moment(this.range.end === null ? undefined : this.range.end)
                    .endOf(DEFAULT_TIME_UNIT)
                    .format()
                    .slice(0, 19),
                this.equipmentTimezone,
            ).format(),
            SINGLE_REPORT_NAME,
        );
    }

    public downloadLocationExcel(locationGuid: string) {
        return HaccpService.exportLocationReport(
            locationGuid,
            DEFAULT_GRANULARITY,
            DEFAULT_TEMPERATURE_UNIT,
            moment.tz(
                moment(this.range.start === null ? undefined : this.range.start)
                    .startOf(DEFAULT_TIME_UNIT)
                    .format()
                    .slice(0, 19),
                this.equipmentTimezone,
            ).format(),
            moment.tz(
                moment(this.range.end === null ? undefined : this.range.end)
                    .endOf(DEFAULT_TIME_UNIT)
                    .format()
                    .slice(0, 19),
                this.equipmentTimezone,
            ).format(),
        );
    }

    public getHaccpConfigsByLocation = (locationGuid: string, start: Date, end: Date)
        : Promise<LocationHaccpResponse[]> => {
        return HaccpService.getLocation(locationGuid).then(
            (result) => {
                this.equipmentTimezone = result.core.timezoneName;

                return HaccpService.getLocationHaccp(
                    locationGuid,
                    moment.tz(
                        moment(this.range.start === null ? undefined : this.range.start)
                            .startOf(DEFAULT_TIME_UNIT)
                            .format()
                            .slice(0, 19),
                        this.equipmentTimezone,
                    ).format(),
                    moment.tz(
                        moment(this.range.end === null ? undefined : this.range.end)
                            .endOf(DEFAULT_TIME_UNIT)
                            .format()
                            .slice(0, 19),
                        this.equipmentTimezone,
                    ).format(),
                    DEFAULT_TEMPERATURE_UNIT,
                )
                    .then((configurations) => {
                        return configurations.map((response) => {
                            return {
                                ...response,
                                configurations: response.configurations.map((conf: HaccpConfigurationWithReports) => {
                                    return HaccpController.assignColorToHaccpConfigRecords({
                                        ...conf.configuration,
                                        records: conf.report.records,
                                    }, conf.report.configurations);
                                }),
                            };
                        });
                    });
            },
        );

    }

    public addRecordsToHaccpConfig = (start, end) => async (haccpConfigs) => {
        const configured: HaccpConfiguration[] = [];
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < haccpConfigs.length; i++) {
            try {
                const report = await HaccpService.getHaccpConfigReport(
                    haccpConfigs[i].equipmentGuid,
                    haccpConfigs[i].targetDiagnosticType.guid,
                    DEFAULT_GRANULARITY,
                    DEFAULT_TEMPERATURE_UNIT,
                    moment.tz(
                        moment(start === null ? undefined : start)
                            .startOf(DEFAULT_TIME_UNIT)
                            .format()
                            .slice(0, 19),
                        this.equipmentTimezone,
                    ).format(),
                    moment.tz(
                        moment(end === null ? undefined : end)
                            .endOf(DEFAULT_TIME_UNIT)
                            .format()
                            .slice(0, 19),
                        this.equipmentTimezone,
                    ).format(),
                );
                const configurations = report.configurations;
                configured.push(HaccpController.assignColorToHaccpConfigRecords({
                    ...haccpConfigs[i],
                    records: report.records,
                }, configurations));
            } catch (e) {
                configured.push({
                    ...haccpConfigs[i],
                    records: fillArray<any>(
                        numberOfDays(this.range.end, this.range.start),
                        undefined,
                    ),
                });
            }
        }
        return configured;
    }

    public getHaccpConfig = (equipmentID: string, start: Date, end: Date)
        : Promise<[DiagnosticType[], HaccpConfiguration[], EquipmentModel]> => {
        return HaccpService.getEquipmentInfo(equipmentID)
            .then(({equipmentGuid}) => {
                return HaccpService.getEquipmentData(equipmentGuid)
                    .then((result) => {
                        this.equipmentTimezone = result.equipmentInstallation.plant.location.timezoneName as string;
                        const installationDate = result.equipmentInstallation.installationDate;
                        if (installationDate) {
                            this.installationDate.next(
                                new Date(
                                    moment(installationDate.replace(/\[(.*)\]/, ""))
                                        .startOf(DEFAULT_TIME_UNIT).date(),
                                ),
                            );
                        }
                        return {equipmentGuid};
                    });
            })
            .then(({equipmentGuid}) => {
                this.equipmentGUID = equipmentGuid;
                return Promise.all([
                    HaccpService.getEquipmentDiagnosticsTypes(equipmentGuid, true)
                        .then((result) => result.list as DiagnosticType[])
                        .then((list) => list.filter((probe) => probe.supportsHaccp)),
                    HaccpService.getEquipmentHaccp(
                        equipmentGuid,
                        undefined,
                        undefined,
                        DEFAULT_TEMPERATURE_UNIT,
                    )
                        .then((result) => result.list as HaccpConfiguration[])
                        .then(this.addRecordsToHaccpConfig(start, end)),
                    HaccpService.getEquipmentModel(equipmentGuid)
                        .then((result) => result as EquipmentModel),
                ]);
            });
    }

    public changeDate = (newHaccpDate: { start: Date, end: Date }) => {
        // Part of this method has been altered in order to comply with etui's DateRangePicker logic
        if ((!newHaccpDate.start || !newHaccpDate.end)) return;
        if (newHaccpDate.start.getTime() === newHaccpDate.end.getTime()) {
            newHaccpDate.start = moment(newHaccpDate.start).startOf("day").toDate();
            newHaccpDate.end = moment(newHaccpDate.start).endOf("day").toDate();
        }
        this.range = newHaccpDate;
        this.haccpDate.next(newHaccpDate);
    }

    public listenToDate = (listener: (d: { start: Date, end: Date }) => void) => {
        return this.haccpDate.subscribe(listener);
    }

    public listenToInstallationDate = (listener: (d: Date) => void) => {
        return this.installationDate.subscribe(listener);
    }

    public toggleExtremes = (visible: boolean) => {
        this.extremesVisibility = visible;
        this.showExtremes.next(visible);
    }

    public listenToExtremesVisibility = (listener: (visibility: boolean) => void) => {
        return this.showExtremes.subscribe(listener);
    }

    public dispose = () => {
        this.haccpDate.unsubscribe();
        this.showExtremes.unsubscribe();
    }
}
